/**
 * API Service for fetching articles from Django backend
 *
 * All data calls now go to /internal/ (Django views querying DB directly).
 * /api/ is blocked by Nginx — never call it from the browser.
 */

const API_BASE_URL = '/internal';

/**
 * Fetch random articles from the API
 */
export const getRandomArticles = async (count = 20) => {
  try {
    const response = await fetch(`${API_BASE_URL}/articles/?page_size=${count}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('API Response:', data);

    return data.results || [];
  } catch (error) {
    console.error('Error fetching articles:', error);
    throw error;
  }
};

/**
 * Fetch filtered articles with source, date range, sort, search, and pagination.
 *
 * @param {Object} params
 * @param {string} params.source      - source slug, e.g. 'the-war-zone'
 * @param {string} params.search      - free text search
 * @param {string} params.ordering    - e.g. '-scraped_at', '-click_count', 'rank'
 * @param {string} params.from_date   - ISO date string, e.g. '2025-01-01'
 * @param {string} params.to_date     - ISO date string
 * @param {number} params.page        - page number (default 1)
 * @param {number} params.page_size   - results per page (default 20)
 */
export const getFilteredArticles = async (params = {}) => {
  try {
    const query = new URLSearchParams();

    if (params.source)    query.set('source',    params.source);
    if (params.search)    query.set('search',    params.search);
    if (params.ordering)  query.set('ordering',  params.ordering);
    if (params.from_date) query.set('from_date', params.from_date);
    if (params.to_date)   query.set('to_date',   params.to_date);
    if (params.page)      query.set('page',      params.page);
    if (params.page_size) query.set('page_size', params.page_size);

    const url = `${API_BASE_URL}/articles/?${query.toString()}`;
    console.log('Fetching filtered articles:', url);

    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const data = await response.json();
    // Returns { count, next, previous, results }
    return data;
  } catch (error) {
    console.error('Error fetching filtered articles:', error);
    throw error;
  }
};

/**
 * Fetch articles by source
 */
export const getArticlesBySource = async (sourceSlug, count = 20) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/articles/?source=${sourceSlug}&page_size=${count}`
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.results || [];
  } catch (error) {
    console.error('Error fetching articles by source:', error);
    throw error;
  }
};

/**
 * Fetch trending articles
 */
export const getTrendingArticles = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/articles/?ordering=rank&page_size=10`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.results || [];
  } catch (error) {
    console.error('Error fetching trending articles:', error);
    throw error;
  }
};

/**
 * Transform articles - Pass through without modification
 */
export const transformArticles = (articles) => {
  if (!Array.isArray(articles)) {
    console.warn('transformArticles received non-array:', articles);
    return [];
  }

  console.log(`Transforming ${articles.length} articles`);
  return articles;
};

/**
 * Fetch a single article by ID
 */
export const getArticle = async (id) => {
  try {
    // Single article detail is served by Django's article_detail view
    // at /article/{id}/ — no internal API endpoint needed.
    // If you need JSON only, add an /internal/articles/{id}/ view.
    const response = await fetch(`/api/articles/${id}/`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching article:', error);
    throw error;
  }
};

/**
 * Search articles
 */
export const searchArticles = async (query) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/articles/?search=${encodeURIComponent(query)}`
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.results || [];
  } catch (error) {
    console.error('Error searching articles:', error);
    throw error;
  }
};

/**
 * Fetch all active sources for the filter dropdown
 */
export const getSources = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/sources/`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.results || data || [];
  } catch (error) {
    console.error('Error fetching sources:', error);
    throw error;
  }
};

/**
 * Track article click
 * NOTE: internal endpoint uses track-click (hyphen), not track_click (underscore)
 */
export const trackArticleClick = async (articleId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/articles/${articleId}/track-click/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': getCsrfToken(),
      },
      credentials: 'include',
    });

    if (!response.ok) {
      console.warn(`Failed to track click for article ${articleId}: ${response.status}`);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Error tracking click:', error);
    return null;
  }
};

/**
 * Get article click statistics
 * NOTE: not exposed via /internal/ — returns null gracefully.
 */
export const getArticleStats = async (articleId) => {
  return null;
};

/**
 * Fetch the featured video for the homepage video section
 */
export const getFeaturedVideo = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/video/featured/`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching featured video:', error);
    return null;
  }
};

/**
 * Fetch active notifications for the ticker bar
 */
export const getNotifications = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/notifications/`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return Array.isArray(data) ? data : data.results || [];
  } catch (error) {
    console.error('Notification fetch error:', error);
    return [];
  }
};

/**
 * Subscribe an email to the newsletter
 * NOTE: /api/subscribe/ is still needed — add to views.py to fully block /api/
 */
export const subscribeEmail = async (email) => {
  const response = await fetch(`${API_BASE_URL}/subscribe/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return response.json();
};

/**
 * Get CSRF token from Django cookie
 */
function getCsrfToken() {
  const name = 'csrftoken';
  let cookieValue = null;

  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }

  return cookieValue || '';
}